import { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline "quick add a source" tool for an EXISTING entry. Calls
 * POST /api/entries/:id/citations to append a single citation without
 * touching the rest of the form. Useful when you want to expand the
 * source list for an already-published entry (citation count → 3+).
 */
export default function QuickAddSource({ entryId, onAdded }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [publisher, setPublisher] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);

  const autofill = async () => {
    if (!url) return;
    setAutoFilling(true);
    try {
      const r = await api.post("/ai/summarize-url", { url });
      if (r.data?.title) setTitle(r.data.title);
      if (r.data?.publisher) setPublisher(r.data.publisher);
      toast.success("Pre-filled from URL");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not analyze URL");
    } finally { setAutoFilling(false); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!entryId || !url) return;
    setLoading(true);
    try {
      const r = await api.post(`/entries/${entryId}/citations`, { url, title, publisher });
      toast.success("Source added (unverified — mark verified to count toward the 3-source rule)");
      setUrl(""); setTitle(""); setPublisher("");
      onAdded?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add source");
    } finally { setLoading(false); }
  };

  if (!entryId) return null;

  return (
    <div className="border-t border-black/10 pt-5 mt-5">
      <div className="overline mb-2">/quick add a source</div>
      <p className="text-xs text-[#555] mb-3">
        Paste a URL — the new source appears <b>unverified</b> at the end of the list.
        For published entries, replacing or removing a citation may demote the entry to
        pending-review per editorial rules.
      </p>
      {/* NOTE: rendered inside the parent entry <form>; nested <form> is invalid HTML and
          React 19 drops the inner onSubmit, so this is a <div> with an explicit click handler. */}
      <div className="space-y-2" data-testid="quick-add-source">
        <div className="flex gap-2">
          <Input
            data-testid="quick-add-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(e); }}
            placeholder="https://…"
            className="rounded-none border-black/30 h-11 flex-1"
          />
          <Button
            type="button"
            variant="outline"
            disabled={!url || autoFilling}
            onClick={autofill}
            data-testid="quick-add-autofill"
            className="rounded-none border-black h-11"
          >
            <Sparkles className="w-4 h-4 mr-1.5" /> {autoFilling ? "…" : "Auto-fill"}
          </Button>
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          <Input
            data-testid="quick-add-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional — defaults to URL)"
            className="rounded-none border-black/30 h-10"
          />
          <Input
            data-testid="quick-add-publisher"
            value={publisher}
            onChange={(e) => setPublisher(e.target.value)}
            placeholder="Publisher (e.g. Reuters, congress.gov)"
            className="rounded-none border-black/30 h-10"
          />
        </div>
        <Button
          type="button"
          onClick={submit}
          disabled={loading || !url}
          className="rounded-none bg-black text-white h-11"
          data-testid="quick-add-submit"
        >
          <Plus className="w-4 h-4 mr-2" /> Add source
        </Button>
      </div>
    </div>
  );
}
